<script setup>
import { computed, ref, watch } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import { trans } from 'laravel-vue-i18n'
import { getFormColor } from '../../../composables/useReservationsCalendar.js'
import { useClientNoteCache } from '../../../composables/useClientNoteCache.js'
import { markdownToHtml } from '../../../../../markdown.js'

const props = defineProps({
    hovered: { type: Object, default: null }, // { reservation, x, y }
})

const reservation = computed(() => props.hovered?.reservation ?? null)
const form = computed(() => reservation.value?.reservation_forms ?? null)
const formColor = computed(() => getFormColor(form.value))

// ─── Hlavní poznámka klienta (ROORI-347) ───────────────────────────

const mainNoteEnabled = usePage().props.user.features?.map((f) => f.feature_name).includes('roori_client_main_note')
const { fetchNote } = useClientNoteCache()

const note = ref(null)
let noteTimer = null

watch(() => reservation.value?.email, (email) => {
    note.value = null
    clearTimeout(noteTimer)

    if (!mainNoteEnabled || !email) {
        return
    }

    noteTimer = setTimeout(async () => {
        const result = await fetchNote(email)
        if (result?.has_note && reservation.value?.email === email) {
            note.value = result
        }
    }, 300)
}, { immediate: true })

const noteHtml = computed(() => note.value?.notes ? markdownToHtml(note.value.notes) : '')

function editNote() {
    if (note.value?.contact_id) {
        router.visit('/app/contacts/' + note.value.contact_id)
    }
}

const statusLabel = computed(() => {
    const map = { accepted: trans('reservations.state_accepted_label'), pending: trans('reservations.state_pending_label'), rejected: trans('reservations.state_rejected_label') }
    return map[reservation.value?.state] ?? ''
})

const dateLabel = computed(() => {
    const r = reservation.value
    if (!r) return ''
    if (r.time_start) {
        return `${r.time_start.substring(0, 5)} – ${r.time_end?.substring(0, 5) ?? ''}`
    }
    if (r.date_start && r.date_end) {
        return `${r.formatted_date} – ${r.formatted_date_end}`
    }
    return r.formatted_date ?? ''
})

const totalPrice = computed(() => {
    const items = reservation.value?.bill_items
    if (!items?.length) return null
    const sum = items.reduce((s, i) => s + i.unit_price_with_vat * i.amount, 0)
    return new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 0 }).format(sum) + ' Kč'
})

// Pozice — flip od pravého okraje
const style = computed(() => {
    if (!props.hovered) return {}
    const { x, y } = props.hovered
    const flipLeft = x + 14 > window.innerWidth - 300
    return {
        position: 'fixed',
        top: `${y + 14}px`,
        left: flipLeft ? `${x - 294}px` : `${x + 14}px`,
        zIndex: 9999,
        pointerEvents: 'none',
    }
})
</script>

<template>
    <Teleport to="body">
        <Transition name="hp">
            <div
                v-if="hovered && reservation"
                :style="style"
                class="w-[280px] bg-white border border-neutral-200 rounded-2xl shadow-[0_24px_60px_-20px_rgba(15,17,21,.28),0_4px_12px_rgba(15,17,21,.07)] px-3.5 py-3 text-sm text-neutral-800"
            >
                <!-- Header: form name + status -->
                <div
                    class="flex items-center justify-between gap-2 pb-2 mb-2 border-b"
                    :style="{ borderColor: formColor + '40' }"
                >
                    <div class="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-600 min-w-0 overflow-hidden">
                        <span
                            class="w-2 h-2 rounded-full flex-shrink-0"
                            :style="{ background: formColor }"
                        />
                        <span class="truncate">{{ form?.name }}</span>
                    </div>
                    <span
                        class="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full flex-shrink-0"
                        :class="{
                            'bg-emerald-100 text-emerald-800': reservation.state === 'accepted',
                            'bg-amber-100 text-amber-700':    reservation.state === 'pending',
                            'bg-red-100 text-red-700':         reservation.state === 'rejected',
                        }"
                    >
                        {{ statusLabel }}
                    </span>
                </div>

                <!-- Title -->
                <div class="text-[15px] font-semibold leading-snug mb-2 tracking-tight">
                    {{ reservation.name }}
                </div>

                <!-- Meta rows -->
                <div class="flex flex-col gap-1.5 text-[12px] text-neutral-600">
                    <div v-if="dateLabel" class="flex items-center gap-1.5">
                        <i class="pi pi-clock !text-[11px] text-neutral-400 flex-shrink-0"/>
                        <span class="font-semibold text-neutral-800">{{ dateLabel }}</span>
                        <span v-if="reservation.date" class="text-neutral-400">· {{ reservation.formatted_date }}</span>
                    </div>

                    <div v-if="reservation.email" class="flex items-center gap-1.5">
                        <i class="pi pi-envelope !text-[11px] text-neutral-400 flex-shrink-0"/>
                        <span>{{ reservation.email }}</span>
                    </div>

                    <div v-if="reservation.workers?.name" class="flex items-center gap-1.5">
                        <i class="pi pi-user !text-[11px] text-neutral-400 flex-shrink-0"/>
                        <span>{{ reservation.workers.name }}</span>
                    </div>

                    <div
                        v-if="totalPrice"
                        class="flex items-center gap-1.5 mt-1 pt-1.5 border-t border-neutral-100 font-semibold text-neutral-800 tabular-nums"
                    >
                        <i class="pi pi-receipt !text-[11px] text-neutral-400 flex-shrink-0"/>
                        {{ totalPrice }}
                    </div>
                </div>

                <!-- Hlavní poznámka klienta -->
                <div
                    v-if="note"
                    class="note-interactive mt-2 pt-2 border-t border-neutral-100"
                >
                    <div class="max-h-[200px] overflow-y-auto text-[12px] text-neutral-700 note-markdown" v-html="noteHtml"></div>
                    <button
                        type="button"
                        class="mt-1.5 text-[11px] font-semibold text-primary-600 hover:underline"
                        @click="editNote"
                    >
                        {{ $t('contacts.tooltip_edit_note') }}
                    </button>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
.hp-enter-active { transition: opacity .1s ease, transform .1s ease; }
.hp-leave-active { transition: opacity .08s ease; }
.hp-enter-from  { opacity: 0; transform: translateY(4px); }
.hp-leave-to    { opacity: 0; }

/* Rodič má pointer-events: none (aby tooltip neblokoval kliky do kalendáře),
   poznámka ale potřebuje být scrollovatelná a odkaz klikatelný. */
.note-interactive {
    pointer-events: auto;
}

.note-markdown:deep(p) { margin-bottom: 0.375rem; }
.note-markdown:deep(ul) { list-style: disc; padding-left: 1.1rem; margin-bottom: 0.375rem; }
.note-markdown:deep(ol) { list-style: decimal; padding-left: 1.1rem; margin-bottom: 0.375rem; }
.note-markdown:deep(code) { background: rgba(0,0,0,.06); border-radius: 4px; padding: 0 4px; }
</style>
